/**
 * What is on sale, and what to do when the customer comes back from Stripe.
 *
 * Pure and dependency-free, like `credits.ts` next door and for the same
 * reason: CRA's jest cannot resolve react-router-dom v7, so anything worth
 * testing has to live outside routed code.
 */
import type { PlanInfo, PlanKey, Product, Sellable } from '../types';

/** The order the ladder reads in. A plan the server does not send is skipped,
 *  so adding one is a server change only. */
export const PLAN_ORDER: PlanKey[] = ['free', 'starter', 'pro', 'enterprise'];

export type PlanOffer = {
  plan: PlanInfo;
  /** The price to send to Checkout, or null when this plan is not for sale. */
  price: Sellable | null;
  /** They are on this plan right now. */
  current: boolean;
};

/** The plan cards for one product, in ladder order.
 *
 *  A card gets a buy button only when the SERVER sent a price for it. That is
 *  what keeps "Boardroom only" out of the page: there is no product check here,
 *  there are simply no prices for anything else.
 */
export function planOffers(
  plans: Record<string, PlanInfo>,
  sellables: Sellable[],
  product: Product,
  currentPlan: PlanKey,
): PlanOffer[] {
  const priceFor = (plan: PlanKey) =>
    sellables.find(
      s => s.kind === 'SUBSCRIPTION' && s.product === product && s.plan === plan,
    ) ?? null;
  return PLAN_ORDER.filter(key => plans[key]).map(key => ({
    plan: plans[key],
    price: priceFor(key),
    current: key === currentPlan,
  }));
}

/** The credit packs on sale, cheapest first. */
export function topupOffers(sellables: Sellable[]): Sellable[] {
  return sellables
    .filter(s => s.kind === 'TOPUP' && (s.credits ?? 0) > 0)
    .sort((a, b) => (a.credits ?? 0) - (b.credits ?? 0));
}

/** "$29/mo", "$10", or null when the amount is unknown. */
export function priceLabel(s: Sellable | null): string | null {
  if (!s || s.unit_amount_usd == null) return null;
  const amount =
    s.unit_amount_usd % 1 === 0
      ? `$${s.unit_amount_usd.toFixed(0)}`
      : `$${s.unit_amount_usd.toFixed(2)}`;
  return s.interval ? `${amount}/${s.interval === 'month' ? 'mo' : s.interval}` : amount;
}

export type CheckoutReturn = 'success' | 'topup' | 'cancel' | null;

/** What Stripe sent them back with. */
export function checkoutReturn(search: string): CheckoutReturn {
  const value = new URLSearchParams(search).get('checkout');
  return value === 'success' || value === 'topup' || value === 'cancel' ? value : null;
}

/** A word for what just happened, or null when there is nothing to say. */
export function returnMessage(kind: CheckoutReturn): string | null {
  if (kind === 'success') return 'Thanks - your plan is active. It may take a moment to show.';
  if (kind === 'topup') return 'Thanks - your credits are on their way.';
  if (kind === 'cancel') return 'No charge was made.';
  return null;
}

/**
 * WHEN TO RE-READ /billing/me AFTER A REDIRECT.
 *
 * The row is written by a webhook, which races the browser: Stripe redirects
 * the customer the instant the payment clears and delivers the event
 * separately, so the first read after landing very often still shows the old
 * plan. There is no entitlement websocket event to wait on — only `credits` —
 * so the page re-reads on a short backoff instead.
 *
 * It stops as soon as something changes, and gives up rather than polling
 * forever: a webhook that has not arrived in ten seconds is a webhook to go and
 * look at, not one to hide behind a spinner.
 */
export const REFRESH_DELAYS_MS = [0, 1500, 4000, 8000];

export function refreshPlan(kind: CheckoutReturn): number[] {
  return kind === 'success' || kind === 'topup' ? REFRESH_DELAYS_MS : [];
}
